/**
 * A colour per node type, and a colour per relationship.
 *
 * Three category colours were not enough to read a real model: a graph with a
 * dozen types drew as three clumps of identical circles, and the only way to
 * tell two apart was to read the label. Giving every type its own hue makes the
 * shape of the model legible before any text is.
 *
 * The colour comes from the type's own id, not from its position in the list,
 * so it is stable: adding a node type never recolours the ones already on the
 * canvas, and the rail, the legend and the circle always agree. Two types can
 * collide onto the same hue on a large model — that costs a moment's confusion,
 * where an unstable palette would cost trust in the whole picture.
 */

/**
 * Vivid enough to hold their own against a dark canvas, and dark enough to keep
 * white-ish labels legible on a light one. Ordered so that neighbouring entries
 * are far apart in hue, which keeps two adjacent nodes distinguishable even
 * when the hash happens to land them side by side.
 */
const NODE_PALETTE = [
  '#5B7CF6', // indigo
  '#22C55E', // green
  '#A78BFA', // violet
  '#F59E0B', // amber
  '#06B6D4', // cyan
  '#F16780', // rose
  '#10D9A0', // teal
  '#E879A9', // pink
  '#5BA8EF', // sky
  '#84CC16', // lime
  '#C084FC', // orchid
  '#FB923C', // orange
  '#2DD4BF', // turquoise
  '#F472B6', // magenta
  '#818CF8', // periwinkle
  '#EAB308', // gold
]

/** Cooler and lower-contrast: an edge should never out-shout a node. */
const EDGE_PALETTE = [
  '#6E7BF2',
  '#3FBF7F',
  '#9C7BE8',
  '#D08A2E',
  '#3AA6C2',
  '#D2647A',
  '#2FB894',
  '#C97BA0',
]

function hash(value: string): number {
  let h = 0
  for (let i = 0; i < value.length; i++) h = (h * 31 + value.charCodeAt(i)) >>> 0
  return h
}

/** The colour of one node type. Stable for the life of the type. */
export function nodeColour(nodeTypeId: string): string {
  return NODE_PALETTE[hash(nodeTypeId) % NODE_PALETTE.length]
}

/**
 * The colour of one relationship, keyed by its name rather than its id, so
 * every `:HAS_CONTACT` edge in the model is drawn the same colour wherever it
 * appears — which is what lets you follow one kind of relationship across a
 * busy diagram.
 */
export function edgeColour(edgeKey: string): string {
  return EDGE_PALETTE[hash(edgeKey) % EDGE_PALETTE.length]
}

/** A lighter tint of a hex colour, for the top-left highlight on a sphere. */
export function lighten(hex: string, amount = 70): string {
  const n = parseInt(hex.slice(1), 16)
  const clamp = (v: number) => Math.min(255, Math.max(0, v))
  return `rgb(${clamp(((n >> 16) & 0xff) + amount)},${clamp(((n >> 8) & 0xff) + amount)},${clamp((n & 0xff) + amount)})`
}

/** A darker shade, for the far edge of the sphere. */
export function darken(hex: string, amount = 40): string {
  return lighten(hex, -amount)
}
